import { Component, OnDestroy, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject, BehaviorSubject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, flatMap, tap, map } from 'rxjs/operators';
import { UserService } from '../services/user.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

@Component({
    styles: [`
        .scroll-container {
            max-height: 250px;
            min-height: 250px;
            height: 250px;
            overflow-y: auto;
        }
    `],
    template: `
        <div mat-dialog-title>Select Members</div>
        <div mat-dialog-content>
            <div fxLayout="column" style="width: 500px;">
                <mat-form-field>
                    <input matInput [formControl]="searchControl" placeholder="Search Members">
                </mat-form-field>
                <div class="scroll-container" fxFlex fxLayout="column" infiniteScroll [infiniteScrollThrottle]="300" [scrollWindow]="false" (scrolled)="nextPage()">
                    <table mat-table 
                           [dataSource]="users"
                           matSort 
                           [matSortActive]="searchParams.value.sort" 
                           [matSortDirection]="searchParams.value.dir"
                           [matSortDisableClear]="true"
                           (matSortChange)="onSort($event)" 
                           fxFlex 
                           width="100%;">
                        <ng-container matColumnDef="firstName">
                            <th mat-header-cell *matHeaderCellDef mat-sort-header="firstNameLower">First Name</th>
                            <td mat-cell *matCellDef="let row">{{row.firstName}}</td>
                        </ng-container>
                        <ng-container matColumnDef="lastName">
                            <th mat-header-cell *matHeaderCellDef mat-sort-header="lastNameLower">Last Name</th>
                            <td mat-cell *matCellDef="let row">{{row.lastName}}</td>
                        </ng-container>
                        <ng-container matColumnDef="emailAddress">
                            <th mat-header-cell *matHeaderCellDef mat-sort-header="emailAddress">Email</th>
                            <td mat-cell *matCellDef="let row">{{row.emailAddress}}</td>
                        </ng-container>
                        <ng-container matColumnDef="action">
                            <th mat-header-cell *matHeaderCellDef></th>
                            <td mat-cell *matCellDef="let row">
                                <mat-checkbox name="isSelected" [(ngModel)]="row.isSelected" (change)="selectChange($event, row)"></mat-checkbox>
                            </td>
                        </ng-container>  
                        <tr mat-header-row *matHeaderRowDef="['firstName', 'lastName', 'emailAddress', 'action']"></tr>
                        <tr mat-row *matRowDef="let row; columns: ['firstName', 'lastName', 'emailAddress', 'action'];"></tr>
                    </table>
                    <div *ngIf="noTouchingPaging" fxLayoutAlign="center center">
                        <spinner></spinner>
                    </div>
                </div>
            </div>
        </div>
        <div mat-dialog-actions>
            <button mat-raised-button color="primary" [mat-dialog-close]="selected" [disabled]="!selected.length">Select</button>
            <button mat-button mat-dialog-close>Cancel</button>
        </div>
    `
})
export class SelectMultipleBriefingMembersDialogComponent implements OnDestroy {
    private cleanupTime = new Subject<void>();
    searchParams: BehaviorSubject<any> = new BehaviorSubject({ page: 0 });
    users = new BehaviorSubject([]);
    selectedUsers: { [key:string]:any }  = {};
    searchControl = new FormControl();
    noTouchingPaging: boolean;
    isLast: any;

    constructor(@Inject(MAT_DIALOG_DATA) data: { briefingId: string }, userService: UserService) {
        this.searchParams
        .pipe(
            tap(() => this.noTouchingPaging = true),
            flatMap(params => userService.searchUsers(params)),
            tap(res => this.isLast = res.last),
            map(res => res.content),
            map(users => users.filter(user => !this.userSubscribed(user, data.briefingId))),
            map(users => users.map(user => {
                user.isSelected = !!this.selectedUsers[user.id];
                return user;
            })),
            tap(() => this.noTouchingPaging = false),
            takeUntil(this.cleanupTime)
        ).subscribe(users => this.users.next(this.users.value.concat(users)));

        this.searchControl.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            tap(() => this.users.next([])),
            takeUntil(this.cleanupTime)
        ).subscribe(searchTerm => this.searchParams.next(Object.assign({}, this.searchParams.value, {searchTerm, page: 0})));
    }

    userSubscribed(user, briefingId): boolean {
        return user.subscriptions?.reduce((accum, sub) => accum || sub.briefingId === briefingId, false);
    }

    nextPage() {
        if (!this.isLast) {
            this.searchParams.next(Object.assign({}, this.searchParams.value, { page: ++this.searchParams.value.page}));
        }
    }

    onSort($event) {
        this.users.next([]);
        this.searchParams.next(Object.assign({}, this.searchParams.value, { page: 0, sort: $event.active, sortDirection: $event.direction}));
    }

    selectChange(event: MatCheckboxChange, row) {
        this.selectedUsers[row.id] = event.checked ? row : undefined;
    }

    get selected() {
        return Object.entries(this.selectedUsers).map(([, val]) => val);
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
        this.searchParams.complete();
        this.users.complete();
    }
};